"use client";

import { type ReactNode, useState, useEffect } from "react";
import { styled, useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import MuiAppBar from "@mui/material/AppBar";
import MuiToolbar from "@mui/material/Toolbar";
import useMediaQuery from "@mui/material/useMediaQuery";
import PusherProvider from "@components/Providers/PusherProvider";
import * as Providers from "@components/Providers";
import * as Nav from "@components/Nav";
import Copyright from "@components/shared/Copyright";
import Logo from "@components/Nav/parts/Logo";
import StatusBar from "@components/StatusBar/StatusBar";
import OnboardingStatusBar from "@components/Onboarding/OnboardingStatusBar";
import OnboardingModal from "@components/Onboarding/OnboardingModal";
import BusinessEmailDialog from "@components/Onboarding/BusinessEmailDialog";
import ConfirmDomainDialog from "@components/Onboarding/ConfirmDomainDialog";
import { OnboardingProvider } from "@/lib/onboarding/OnboardingContext";
import MessageBanner from "@components/shared/AppMessageBanner";
import { usePathname, useRouter } from "next/navigation";
import config from "@lib/config";
import { hasFeatureAccess, FLAG_LEVELS } from "@lib/featureFlags";
import ThirdPartyScripts from "@components/ThirdParty/ThirdPartyScripts";
import CSVBoxStyler from "@components/shared/CSVBoxStyler";
import Toaster from "@components/shared/Toaster";
import ToastHost from "@components/shared/ToastHost";
import {
  SOLAFloatingProvider,
  FloatingContainer,
} from "@/components/sola/components/Floating";
import useRecruiterDetails from "@/lib/user/useRecruiterDetails";
import { ACCOUNT_STATUS } from "@lib/enums";
import { PreviousPageProvider } from "@lib/navigation/PreviousPageContext";

interface DashboardLayoutProps {
  children: ReactNode;
}

interface OpenStyleProps {
  open: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})<OpenStyleProps>(({ theme, open }) => ({
  marginTop: open ? "42px" : "0px",
  position: "fixed",
  boxShadow: "none",
  zIndex: theme.zIndex.drawer + 1,
  transition: "margin-top 0.8s ease",
}));

const Toolbar = styled(MuiToolbar)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  height: "70px",
});

const ContentContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "open",
})<OpenStyleProps>(({ open }) => ({
  display: "flex",
  flexDirection: "row",
  marginTop: open ? "42px" : "0px",
  transition: "margin-top 0.8s ease",
}));

const MainContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "open",
})<OpenStyleProps>(({ open }) => ({
  flexGrow: 1,
  paddingLeft: open ? config.drawerWidth : "77px",
  transition: "padding-left 225ms cubic-bezier(0.4, 0, 0.6, 1)",
  "--sidebar-width": open ? `${config.drawerWidth}px` : "77px",
}));

const WHITELIST_RECRUITER_IDS = [316];

const PageContainer = styled(Box)({
  margin: 0,
  minHeight: "calc(100vh - 70px)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
});

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [leftMenuOpen, setLeftMenuOpen] = useState(true);
  const [statusBarOpen, setStatusBarOpen] = useState(false);
  const [onboardingBarOpen, setOnboardingBarOpen] = useState(false);
  const { recruiter, accountStatus } = useRecruiterDetails();
  const router = useRouter();

  // The sidebar is a "permanent" drawer that pushes content over rather
  // than overlaying it — below `lg` (1200px) that pushed-content width
  // drops under what page content assumes it has (most of the app's own
  // responsive breakpoints are MUI's `md`/900px, measured against the raw
  // viewport, not viewport-minus-sidebar), causing clipping/horizontal
  // scroll. Auto-collapse to icon-only below that width; the user's own
  // toggle still applies once they're back above it.
  const theme = useTheme();
  const isNarrowViewport = useMediaQuery(theme.breakpoints.down("lg"));
  const sidebarOpen = leftMenuOpen && !isNarrowViewport;

  const pathname = usePathname();

  const needsUpgrade = accountStatus === ACCOUNT_STATUS.TRIAL_EXPIRED || accountStatus === ACCOUNT_STATUS.PLAN_EXPIRED;

  useEffect(() => {
    if (needsUpgrade) {
      router.replace("/pricing");
    }
  }, [needsUpgrade, router]);

  // Next.js's App Router only auto-scrolls to the top of a newly rendered
  // page when it isn't already in the viewport at the old scroll position.
  // Since this layout persists across every route, a page shorter than the
  // previous scroll position is left scrolled past its own heading — force
  // it back to the top on every navigation.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  if (needsUpgrade) {
    return null;
  }
  const canAccessSOLA = hasFeatureAccess(FLAG_LEVELS.BETA, recruiter?.user);
  const isOnSOLAPage = pathname?.startsWith("/sola");

  const toggleDrawer = () => {
    setLeftMenuOpen(!leftMenuOpen);
  };

  const anyStatusBarOpen = statusBarOpen || onboardingBarOpen;

  return (
    <PreviousPageProvider>
      <PusherProvider>
        <Providers.Notifications>
          <Providers.Jobs>
            <Providers.SettingsNavigation>
              <OnboardingProvider>
                <SOLAFloatingProvider>
                  <Toaster />
                  <ToastHost />
                  <StatusBar open={statusBarOpen} onOpen={setStatusBarOpen} />
                  <OnboardingStatusBar
                    open={onboardingBarOpen}
                    onOpen={setOnboardingBarOpen}
                  />
                  <OnboardingModal />
                  <BusinessEmailDialog />
                  <ConfirmDomainDialog />
                  <ContentContainer open={anyStatusBarOpen}>
                    <AppBar open={anyStatusBarOpen}>
                      <Toolbar>
                        <Logo />
                        <Nav.Toolbar />
                      </Toolbar>
                    </AppBar>
                    <Nav.Main
                      open={sidebarOpen}
                      statusBarOpen={anyStatusBarOpen}
                      toggleDrawer={toggleDrawer}
                    />
                    <MainContent
                      id="mainContent"
                      as="main"
                      open={sidebarOpen}
                    >
                      <Toolbar />
                      <PageContainer>
                        <Box minHeight="90%">
                          <MessageBanner />
                          {children}
                        </Box>
                        <Copyright sx={{ pt: 4 }} />
                      </PageContainer>
                    </MainContent>
                  </ContentContainer>

                  {canAccessSOLA && !isOnSOLAPage && <FloatingContainer />}
                </SOLAFloatingProvider>
              </OnboardingProvider>
            </Providers.SettingsNavigation>
          </Providers.Jobs>
        </Providers.Notifications>
        <CSVBoxStyler />
        <ThirdPartyScripts />
      </PusherProvider>
    </PreviousPageProvider>
  );
}
